// client/src/components/admin/QuoteManager.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './Manager.css';

const QuoteManager = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All'); // 'All' | 'New' | 'Done'

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes(response.data);
    } catch (err) {
      setError('Failed to fetch quotes. Please try refreshing the page.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this quote request?')) {
      try {
        const token = localStorage.getItem('authToken');
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/quotes/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchQuotes();
      } catch (err) {
        alert('Failed to delete quote. Please try again.');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === 'New' ? 'Done' : 'New';
    try {
      const token = localStorage.getItem('authToken');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/quotes/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchQuotes();
    } catch (err) {
      alert('Failed to update status. Please try again.');
      console.error(err);
    }
  };

  const stats = useMemo(() => {
    const total = quotes.length;
    const pending = quotes.filter((q) => q.status === 'New').length;
    const completed = quotes.filter((q) => q.status === 'Done').length;
    const customCount = quotes.filter((q) => q.is_custom).length;
    return { total, pending, completed, customCount };
  }, [quotes]);

  const filteredQuotes = useMemo(() => {
    return quotes.filter((q) => {
      const matchesStatus = statusFilter === 'All' || q.status === statusFilter;
      const term = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !term ||
        (q.product_name && q.product_name.toLowerCase().includes(term)) ||
        (q.customer_name && q.customer_name.toLowerCase().includes(term)) ||
        (q.customer_company && q.customer_company.toLowerCase().includes(term)) ||
        (q.customer_email && q.customer_email.toLowerCase().includes(term)) ||
        (q.customer_phone && q.customer_phone.toLowerCase().includes(term));
      return matchesStatus && matchesSearch;
    });
  }, [quotes, statusFilter, searchQuery]);

  const renderTableRows = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="6" className="admin-empty-cell">
            <i className="fas fa-circle-notch fa-spin"></i>
            Loading quote requests...
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <td colSpan="6" className="error-message">
            {error}
          </td>
        </tr>
      );
    }
    if (filteredQuotes.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="admin-empty-cell">
            <i className="fas fa-inbox"></i>
            No matching quote requests found.
          </td>
        </tr>
      );
    }

    return filteredQuotes.map((quote) => (
      <tr key={quote.id}>
        <td>
          <span className="cell-primary-text">
            {quote.product_name}
            {quote.is_custom ? <span className="custom-tag-badge">Custom</span> : null}
          </span>
          {quote.created_at && (
            <span className="cell-secondary-text">
              <i className="far fa-calendar-alt"></i>{' '}
              {new Date(quote.created_at).toLocaleDateString()}
            </span>
          )}
        </td>
        <td>
          <span className="qty-badge">{quote.quantity}</span>
        </td>
        <td>
          <span className="cell-primary-text">{quote.customer_name}</span>
          {quote.customer_company && (
            <span className="cell-secondary-text">{quote.customer_company}</span>
          )}
        </td>
        <td>
          <div className="contact-links-stack">
            <a href={`mailto:${quote.customer_email}`}>
              <i className="far fa-envelope"></i> {quote.customer_email}
            </a>
            <a href={`tel:${quote.customer_phone}`}>
              <i className="fas fa-phone-alt"></i> {quote.customer_phone}
            </a>
          </div>
        </td>
        <td>
          <span className={`status ${quote.status}`}>
            <i
              className={`fas ${
                quote.status === 'Done' ? 'fa-check-circle' : 'fa-clock'
              }`}
            ></i>
            {quote.status}
          </span>
        </td>
        <td>
          <div className="actions-cell">
            <button
              onClick={() => handleStatusToggle(quote.id, quote.status)}
              className={`btn-status-toggle ${quote.status === 'New' ? 'new' : 'done'}`}
            >
              <i
                className={`fas ${quote.status === 'New' ? 'fa-check' : 'fa-undo'}`}
              ></i>
              {quote.status === 'New' ? 'Mark Done' : 'Reopen'}
            </button>
            <button
              onClick={() => handleDelete(quote.id)}
              className="btn-delete"
              title="Delete quote"
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="manager-container">
      <div className="manager-page-header">
        <div className="manager-title-group">
          <h1>Quote Requests</h1>
          <p>Review and fulfill equipment quote inquiries from hospitals and clinics.</p>
        </div>
        <div className="manager-header-actions">
          <button type="button" className="btn-refresh" onClick={fetchQuotes}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      {/* --- KPI Summary Cards --- */}
      <div className="admin-kpi-grid">
        <div className="admin-kpi-card">
          <div className="kpi-icon-box blue">
            <i className="fas fa-file-invoice"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Total Inquiries</span>
            <span className="kpi-value">{stats.total}</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box amber">
            <i className="fas fa-hourglass-half"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">New / Pending</span>
            <span className="kpi-value">{stats.pending}</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box green">
            <i className="fas fa-check-double"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Fulfilled (Done)</span>
            <span className="kpi-value">{stats.completed}</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="kpi-icon-box burgundy">
            <i className="fas fa-Tools fa-sliders-h"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-label">Custom Requests</span>
            <span className="kpi-value">{stats.customCount}</span>
          </div>
        </div>
      </div>

      {/* --- Search & Status Filter Toolbar --- */}
      <div className="admin-toolbar">
        <div className="admin-search-box">
          <i className="fas fa-search"></i>
          <input
            type="search"
            placeholder="Search by product, customer name, hospital, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="admin-filter-group">
          {['All', 'New', 'Done'].map((status) => (
            <button
              key={status}
              type="button"
              className={`filter-pill-btn ${statusFilter === status ? 'active' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              {status === 'All' ? 'All Quotes' : status}
            </button>
          ))}
        </div>
      </div>

      <div className="manager-table-wrapper">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Product & Date</th>
              <th>Qty</th>
              <th>Customer / Facility</th>
              <th>Contact Details</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{renderTableRows()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default QuoteManager;
